import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ChevronLeft, Loader2, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BACKEND } from "../../connect/api";

dayjs.extend(relativeTime);

const HUES = ["#3b82f6", "#22c55e", "#f59e0b", "#a855f7", "#ec4899", "#14b8a6", "#f97316", "#bd8931"];
export const hueFor = (s = "") => HUES[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % HUES.length];
export const initialsOf = (name = "") => name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "?";

export const Avatar = ({ name, src, size = 40, ceo, className = "" }) => {
  const [broken, setBroken] = useState("");
  const url = src && broken !== src ? (src.startsWith("/api/") ? `${BACKEND}${src}` : src) : "";
  return (
    <div className={`shrink-0 rounded-full flex items-center justify-center font-bold border overflow-hidden ${className}`} data-testid="avatar"
      style={{ width: size, height: size, fontSize: size * 0.36, background: ceo ? "rgba(189,137,49,0.18)" : "#0b1322", color: ceo ? "#e7c477" : hueFor(name), borderColor: ceo ? "rgba(189,137,49,0.5)" : "rgba(59,130,246,0.2)" }}>
      {url ? <img src={url} alt={name} decoding="async" onError={() => setBroken(src)} className="w-full h-full object-cover object-top" /> : initialsOf(name)}
    </div>
  );
};

export const ago = (iso) => (iso ? dayjs(iso).fromNow() : "");
export const CEO_TITLE = "Group CEO";
export const CEO_ORG = "NexSecure Group";
/** "Title · Department" for staff; the CEO is always shown as Group CEO. */
export const roleLine = (s, withOffice = false) => (!s ? "" : s.is_ceo ? CEO_TITLE : [s.title, s.department, withOffice && s.office ? s.office.replace("NST | ", "") : ""].filter(Boolean).join(" · "));
export const clock = (iso) => (iso ? dayjs(iso).format("h:mm A") : "");
export const dayLabel = (iso) => (iso ? dayjs(iso).format("DD MMM, h:mm A") : "");

export const Spinner = ({ className = "" }) => <Loader2 size={18} className={`animate-spin text-blue-400 ${className}`} />;

export const Header = ({ title, sub, back, right }) => {
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0e1626]/85 border-b border-white/10 px-3 py-2.5 flex items-center gap-2 min-h-[56px]" data-testid="connect-header">
      {back && (
        <button onClick={() => nav(back === true ? -1 : back)} className="p-2 -ml-1 rounded-full hover:bg-white/10 active:bg-white/15 lg:hidden" data-testid="header-back-button" aria-label="Back">
          <ChevronLeft size={22} />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <h1 className="nxc-display text-lg font-semibold leading-tight truncate">{title}</h1>
        {sub && <div className="text-xs text-slate-400 truncate">{sub}</div>}
      </div>
      {right}
    </header>
  );
};

export const Empty = ({ icon: Icon, title, text }) => (
  <div className="flex flex-col items-center text-center px-8 py-16 text-slate-400 nxc-fade-in" data-testid="empty-state">
    {Icon && <Icon size={36} className="text-slate-600 mb-3" />}
    <div className="text-slate-200 font-semibold">{title}</div>
    {text && <div className="text-sm mt-1">{text}</div>}
  </div>
);

export const Badge = ({ n, gold }) =>
  n > 0 ? <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${gold ? "bg-amber-500 text-black" : "bg-blue-500 text-white"}`} data-testid="badge">{n > 99 ? "99+" : n}</span> : null;

/** Paginated list loader: fetcher(page) -> {items, has_more}. Resets when deps change. */
export function usePaged(fetcher, deps) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [raw, setRaw] = useState(null);
  const busy = useRef(false);

  const load = useCallback(async (p, replace) => {
    if (busy.current) return;
    busy.current = true;
    setLoading(true);
    try {
      const d = await fetcher(p);
      setItems((prev) => (replace ? d.items : [...prev, ...d.items]));
      setHasMore(!!d.has_more); setRaw(d);
      setPage(p);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      busy.current = false;
      setLoading(false);
    }
  }, [fetcher]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setItems([]); load(1, true); }, deps);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (hasMore && !busy.current && el.scrollTop + el.clientHeight >= el.scrollHeight - 240) load(page + 1);
  };
  const reload = useCallback(() => load(1, true), [load]);
  return { items, setItems, hasMore, loading, error, raw, total: raw?.total, onScroll, loadMore: () => load(page + 1), reload };
}

export const LoadMore = ({ hasMore, loading, onClick }) => (
  <div className="py-4 flex justify-center">
    {loading ? <Spinner /> : hasMore ? <button onClick={onClick} className="nxc-btn nxc-btn-ghost text-xs" data-testid="load-more-button">Load more</button> : null}
  </div>
);

/** Horizontally scrollable chip row: touch-scroll on mobile, mouse-wheel + arrow buttons on desktop, visible on every width. */
export const ChipRow = ({ children, className = "", testid }) => {
  const ref = useRef();
  const drag = useRef(null);
  const suppress = useRef(false);
  const [edges, setEdges] = useState({ l: false, r: false });
  const update = () => { const el = ref.current; if (!el) return; setEdges({ l: el.scrollLeft > 4, r: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 }); };
  useEffect(() => { update(); const el = ref.current; if (!el) return; const ro = new ResizeObserver(update); ro.observe(el); return () => ro.disconnect(); }, [children]);
  const by = (dx) => ref.current?.scrollBy({ left: dx, behavior: "smooth" });
  // mouse / pen drag-to-scroll (touch uses native panning via touch-action)
  const down = (e) => { if (e.pointerType === "touch") return; drag.current = { x: e.clientX, left: ref.current.scrollLeft, moved: false }; };
  const move = (e) => { const d = drag.current; if (!d) return; const dx = e.clientX - d.x; if (Math.abs(dx) > 3) d.moved = true; ref.current.scrollLeft = d.left - dx; };
  const up = () => { if (drag.current?.moved) suppress.current = true; drag.current = null; };
  const clickCap = (e) => { if (suppress.current) { e.preventDefault(); e.stopPropagation(); suppress.current = false; } };
  return (
    <div className={`relative min-w-0 ${className}`} data-testid={testid}>
      {edges.l && <button type="button" onClick={() => by(-240)} className="flex absolute left-0 top-0 bottom-0 w-7 sm:w-8 items-center justify-start pl-0.5 sm:pl-1 bg-gradient-to-r from-[#0e1626] via-[#0e1626]/90 to-transparent z-10 text-slate-200 !min-h-0" aria-label="Scroll left" data-testid={testid ? `${testid}-left` : undefined}><ChevronLeft size={16} /></button>}
      <div ref={ref} onScroll={update} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up} onClickCapture={clickCap}
        onWheel={(e) => { if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) { ref.current.scrollLeft += e.deltaY; } }} className="flex gap-1.5 overflow-x-auto overflow-y-hidden nxc-chip-scroll px-3 py-0.5 w-full max-w-full" style={{ touchAction: "pan-x pan-y", overscrollBehaviorX: "contain", WebkitOverflowScrolling: "touch" }}>
        {children}
      </div>
      {edges.r && <button type="button" onClick={() => by(240)} className="flex absolute right-0 top-0 bottom-0 w-7 sm:w-8 items-center justify-end pr-0.5 sm:pr-1 bg-gradient-to-l from-[#0e1626] via-[#0e1626]/90 to-transparent z-10 text-slate-200 !min-h-0" aria-label="Scroll right" data-testid={testid ? `${testid}-right` : undefined}><ChevronRight size={16} /></button>}
    </div>
  );
};
